import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, FileText, Search, Image as ImageIcon, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ImageCropper from "@/components/ImageCropper";
import { useNavigate } from "react-router-dom";

const blank = () => ({ model_number: "", name: "", category: "", specs: [], images: [] });

const ProductsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [parsing, setParsing] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/products");
      setItems(data);
    } catch {
      toast.error("Failed to load products");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...blank(), ...p }); setOpen(true); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSpec = (i, k, v) =>
    setForm((f) => {
      const specs = [...f.specs];
      specs[i] = { ...specs[i], [k]: v };
      return { ...f, specs };
    });
  const addSpec = () => setForm((f) => ({ ...f, specs: [...f.specs, { label: "", value: "" }] }));
  const removeSpec = (i) => setForm((f) => ({ ...f, specs: f.specs.filter((_, idx) => idx !== i) }));

  const handleImageFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image too large (max 5MB)");
    const reader = new FileReader();
    reader.onload = (e) => { setCropSrc(e.target.result); setCropOpen(true); };
    reader.readAsDataURL(file);
  };
  const handleCropSave = (dataUrl) => {
    setForm((f) => ({ ...f, images: [...(f.images || []), dataUrl] }));
    setCropOpen(false);
    setCropSrc("");
  };
  const handleCropCancel = () => { setCropOpen(false); setCropSrc(""); };
  const removeImage = (i) =>
    setForm((f) => ({ ...f, images: (f.images || []).filter((_, idx) => idx !== i) }));

  const handlePdfUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Upload a PDF file");
    setParsing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiClient.post("/products/parse-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        model_number: data.model_number || f.model_number,
        specs: data.specs?.length ? data.specs : f.specs,
      }));
      toast.success(`Extracted ${data.specs?.length || 0} spec rows. Review and save.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF parse failed");
    }
    setParsing(false);
  };

  const submit = async () => {
    if (!form.model_number.trim()) return toast.error("Model number is required");
    try {
      if (editing) {
        await apiClient.put(`/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await apiClient.post("/products", form);
        toast.success("Product saved");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/products/${id}`);
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = items.filter((p) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      p.model_number?.toLowerCase().includes(t) ||
      p.name?.toLowerCase().includes(t) ||
      p.category?.toLowerCase().includes(t)
    );
  });

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold ep-text-primary">Product Master / Data Sheets</h1>
          <p className="mt-1 text-sm text-slate-500">
            Save each product once. When you type its model number on a quote, the data sheet auto-fills.
          </p>
        </div>
        {isAdmin && (
          <Button className="rounded-sm ep-amber hover:ep-amber" onClick={openCreate} data-testid="add-product-button">
            <Plus className="mr-2 h-4 w-4" /> Add Product
          </Button>
        )}
      </div>

      <Card className="rounded-sm border-[#E5E7EB] shadow-none">
        <div className="border-b border-[#E5E7EB] p-4">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search model, name, category…"
              className="rounded-sm pl-9"
              data-testid="product-search-input"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="products-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Model Number</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Specs</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                    {items.length === 0
                      ? "No products yet. Add your first data sheet."
                      : "No products match your search."}
                  </td>
                </tr>
              ) : filtered.map((p) => (
                <tr key={p.id} className="border-b border-[#E5E7EB] hover:bg-slate-50" data-testid={`product-row-${p.model_number}`}>
                  <td className="px-4 py-3 font-medium ep-text-primary">{p.model_number}</td>
                  <td className="px-4 py-3 text-slate-700">{p.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.category || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{p.specs?.length || 0} rows</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 rounded-sm text-xs"
                        onClick={() => nav(`/products/${p.id}/datasheet?autoprint=1`)}
                        title="Generate a clean product datasheet PDF on company letterhead"
                        data-testid={`generate-pdf-${p.model_number}`}
                      >
                        <Download className="mr-1 h-3.5 w-3.5" /> Generate PDF
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => openEdit(p)} data-testid={`edit-product-${p.model_number}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700" data-testid={`delete-product-${p.model_number}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-sm">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete product {p.model_number}?</AlertDialogTitle>
                              <AlertDialogDescription>This cannot be undone. Existing quotes are not affected.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(p.id)} className="rounded-sm bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit Product — ${editing.model_number}` : "Add Product / Upload Data Sheet"}</DialogTitle>
          </DialogHeader>

          <div className="rounded-sm border border-dashed border-[#E5E7EB] bg-slate-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-700">Upload Data Sheet PDF</div>
                <div className="text-xs text-slate-500">We&apos;ll extract the model number and spec rows automatically. You can edit them below.</div>
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handlePdfUpload(f); }}
                  data-testid="product-pdf-input"
                />
                <Button asChild variant="outline" size="sm" className="rounded-sm" disabled={parsing}>
                  <span>
                    {parsing ? (
                      <><FileText className="mr-2 h-4 w-4 animate-pulse" /> Parsing…</>
                    ) : (
                      <><Upload className="mr-2 h-4 w-4" /> Choose PDF</>
                    )}
                  </span>
                </Button>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="md:col-span-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Model Number *</Label>
              <Input value={form.model_number} onChange={(e) => set("model_number", e.target.value)} className="mt-1 rounded-sm" data-testid="product-model-input" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Product Name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 rounded-sm" data-testid="product-name-input" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Category</Label>
              <Input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Plastic Storage Bin" className="mt-1 rounded-sm" data-testid="product-category-input" />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Product Photos</div>
                <div className="text-[10px] text-slate-500">Attached photos auto-fill the description image column when this data sheet is applied to a quote.</div>
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleImageFile(f); }}
                  data-testid="product-image-input"
                />
                <Button asChild variant="outline" size="sm" className="rounded-sm">
                  <span><ImageIcon className="mr-1 h-3.5 w-3.5" /> Add Photo</span>
                </Button>
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {(form.images || []).length === 0 ? (
                <div className="w-full rounded-sm border border-dashed border-[#E5E7EB] p-3 text-center text-xs text-slate-400">
                  No photos attached.
                </div>
              ) : (form.images || []).map((src, i) => (
                <div key={i} className="relative h-24 w-32 overflow-hidden rounded-sm border border-[#E5E7EB] bg-white" data-testid={`product-image-${i}`}>
                  <img src={src} alt="" className="h-full w-full object-contain" />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute right-0.5 top-0.5 rounded-sm bg-white/90 px-1 text-[10px] text-red-600 hover:bg-white"
                    data-testid={`product-image-remove-${i}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Spec Rows</div>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={addSpec} data-testid="add-product-spec">
                <Plus className="mr-1 h-3 w-3" /> Row
              </Button>
            </div>
            <div className="max-h-80 space-y-1.5 overflow-y-auto">
              {form.specs.length === 0 && (
                <div className="rounded-sm border border-dashed border-[#E5E7EB] p-3 text-center text-xs text-slate-400">
                  No specs. Upload a PDF or add rows manually.
                </div>
              )}
              {form.specs.map((sp, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <Input value={sp.label} onChange={(e) => setSpec(i, "label", e.target.value)} placeholder="Label" className="col-span-4 rounded-sm" data-testid={`product-spec-label-${i}`} />
                  <Input value={sp.value} onChange={(e) => setSpec(i, "value", e.target.value)} placeholder="Value" className="col-span-7 rounded-sm" data-testid={`product-spec-value-${i}`} />
                  <Button variant="ghost" size="icon" className="col-span-1 h-9 w-9 rounded-sm text-red-600 hover:text-red-700" onClick={() => removeSpec(i)} data-testid={`product-spec-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} className="rounded-sm ep-amber hover:ep-amber" data-testid="save-product-button">
              {editing ? "Save Changes" : "Save Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageCropper
        open={cropOpen}
        src={cropSrc}
        onSave={handleCropSave}
        onCancel={handleCropCancel}
      />
    </div>
  );
};

export default ProductsPage;
